<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { ref } from "vue";
import Button from "@/design/primitives/Button.vue";
import Modal from "@/design/primitives/Modal.vue";
import { HttpError } from "@/shared/http/client";
import { type DocumentVersion, saveDocument } from "../api/document-content";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

const props = defineProps<{ file: DocumentVersion; content: string }>();
const emit = defineEmits<{ close: []; saved: [] }>();
const { t } = useAgentsI18n();
const client = useQueryClient();
const draft = ref(props.content);
const saving = ref(false);
const error = ref("");
async function save(): Promise<void> {
	if (saving.value) return;
	saving.value = true;
	error.value = "";
	try {
		await saveDocument(props.file.path, draft.value, props.file.mimeType, props.file.version);
		void client.invalidateQueries({ queryKey: ["files"] });
		emit("saved");
	} catch (cause) {
		error.value = t(
			cause instanceof HttpError && cause.status === 409
				? "document.conflict"
				: "document.saveFailed",
		);
	} finally {
		saving.value = false;
	}
}
</script>
<template>
	<Modal :open="true" :title="t('document.edit')" :description="file.path" size="lg" :close-label="t('document.cancel')" @update:open="!saving && emit('close')">
		<textarea v-model="draft" class="h-[55dvh] w-full resize-none rounded border border-border bg-surface p-3 font-mono text-xs" :aria-label="t('document.source')" :disabled="saving" />
		<p v-if="error" role="alert" class="text-sm text-danger">{{ error }}</p>
		<template #footer>
			<Button variant="ghost" :disabled="saving" @click="emit('close')">{{ t('document.cancel') }}</Button>
			<Button :disabled="saving || draft === content" @click="save">{{ t('document.save') }}</Button>
		</template>
	</Modal>
</template>
